import { EnvironmentInjector, Injectable, inject } from '@angular/core';
import { fromJson, toJson } from '@bufbuild/protobuf';
import { NbToastrService } from '@nebular/theme';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  CollectionNames,
  ProjectServiceCreateResponseJson,
  ProjectServiceCreateResponseSchema,
  ProjectServiceListRequestJson,
  ProjectServiceListRequestSchema,
  defaultApiSerializeJsonOptions,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { AuthRunEffects } from '@vmspdb/ui/store';
import { EMPTY, Observable, of, tap } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { ProjectRepositoryService, withSearchSync } from '../../repositories';
import { UtilitiesFacade } from '../utilities/utilities.facade';
import { PaginatedProjectActions } from './paginated-project.actions';

@Injectable()
export class PaginatedProjectEffects extends AuthRunEffects {
  private injector = inject(EnvironmentInjector);
  private errors = inject(GlobalErrorHandlerService);
  private repository = inject(ProjectRepositoryService);
  private notifications = inject(NbToastrService);
  private utilities = inject(UtilitiesFacade);

  listPaginatedProjects$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        PaginatedProjectActions.listPaginatedProjects,
        PaginatedProjectActions.cancel,
      ),
      switchMap((action) =>
        action.type === PaginatedProjectActions.cancel.type
          ? EMPTY
          : of(action).pipe(this.performSearch()),
      ),
    );
  });

  performSearch() {
    return (
      source: Observable<{
        params: ProjectServiceListRequestJson;
      }>,
    ) =>
      source.pipe(
        debounceTime(250),
        withSearchSync(CollectionNames.Projects, this.injector),
        tap(() => this.utilities.queue()),
        switchMap((payload) => {
          return this.repository.list(
            fromJson(ProjectServiceListRequestSchema, payload.params),
          );
        }),
        tap((data) => {
          if (data.degraded) {
            this.errors.reportSearchDegradation(CollectionNames.Projects);
            this.notifications.warning(
              'There was an error with the search server for fetching projects.',
              'Search',
              {
                duration: 5000,
              },
            );
          }
        }),
        map((data) =>
          PaginatedProjectActions.listPaginatedProjectsSuccess({
            data: {
              ...data,
              items: (data.items ?? []).map(
                (item) =>
                  toJson(
                    ProjectServiceCreateResponseSchema,
                    item,
                    defaultApiSerializeJsonOptions,
                  ) as ProjectServiceCreateResponseJson,
              ),
            },
          }),
        ),
        catchError((error) => {
          this.errors.reportCaughtError(error);
          return of(
            PaginatedProjectActions.listPaginatedProjectsFailure(error),
          );
        }),
        tap(() => this.utilities.complete()),
      );
  }

  constructor() {
    const actions$ = inject(Actions);

    super(actions$);
  }
}
